/**
 * Doors data hooks.
 *
 * Provides:
 * - `useDoors()` — site-scoped TanStack Query for door live state
 * - `useRequestDoor()` — mutation for `POST /doors/{name}/request` (open/close)
 *
 * The REST seed reads the real v1 `doors` category snapshot
 * (`fetchDoors` → `v1-zenoh-client.ts`, via `doorsQueryOptions` so the
 * query key is the same site-/category-/epoch-aware `v1Key()` the
 * live overlay in `useLiveData` writes into — the two paths must
 * never diverge (plan §6.2)).
 *
 * The `doors_registry` read alongside it is what decides which doors
 * EXIST. Live state alone could not: when the MEGAZO adapter at
 * sg-nomad-01 could not log in to its vendor cloud it published no
 * state at all, so the doors category held zero keys and the console
 * showed no door — indistinguishable, to the engineer standing in
 * front of `L4_SLL_Door`, from a building that has none. The registry
 * comes from the building map and knew about the door the whole time.
 * See `registry-merge.ts` for the merge rule.
 *
 * Door **geometry** for a LIVE door (the line-segment endpoints) is
 * still not merged in — `adaptV1DoorState` in `rmf-client.ts` leaves
 * `level`/`v1`/`v2`/`doorType` unset rather than guessing, and that
 * remains a map-parity follow-up. Only registry-ONLY rows carry the
 * registry's own fields, because for them it is all there is.
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSafeSiteId } from '../../state/useActiveSite';
import {
  doorsQueryOptions,
  doorsRegistryQueryOptions,
  doorHealthQueryOptions,
  doorWriteGatesQueryOptions,
} from '../query-options';
import { armDoorWrites, declareDoorOpen, doorCommand, revokeDoorOverride } from '../v1-client';
import type {
  DoorArmRequest,
  DoorArmResponse,
  DoorHealth,
  DoorWriteGate,
} from '../v1-client';
import { mergeDoorsWithRegistry } from '../registry-merge';
import type {
  DoorCommandResponse,
  DoorOverrideAttestation,
  DoorOverrideResponse,
  DoorRegistry,
  SiteId,
} from '../v1';
import type { DoorViewModel } from '../adapters';

export function useDoors() {
  const siteId = useSafeSiteId();
  const site = siteId ? (siteId as SiteId) : null;
  const live = useQuery<DoorViewModel[]>(doorsQueryOptions(site));
  // A SECOND query rather than a registry fetch folded into
  // `doorsQueryOptions`' queryFn, because the doors key is not this
  // hook's alone: `useLiveData` writes the SSE overlay straight into
  // it, and anything the queryFn had merged in would be replaced by the
  // next live event. Merging here, downstream of both, is the only
  // place the registry rows survive an event arriving.
  const registry = useQuery<DoorRegistry[]>(doorsRegistryQueryOptions(site));
  const data = useMemo(
    () => mergeDoorsWithRegistry(live.data, registry.data),
    [live.data, registry.data],
  );
  // The live query's own status is passed through untouched — a failed
  // door read is still an error the screen must report, and the
  // registry succeeding does not make it a success. `data` is the one
  // thing overridden, and it is never undefined now, so a caller's
  // `?? []` fallback simply never fires.
  return { ...live, data };
}

export interface DoorRequest {
  mode: 'open' | 'close';
}

/**
 * `POST /api/v1/sites/{site}/doors/{door}/command` (plan §Command
 * lifecycle). Replaces the legacy `requestDoor()`/`rmfEndpoints
 * .doorRequest()` path — the v1 command client generates a fresh
 * idempotency key per call and returns a `CommandRecord` whose
 * `state` (`accepted` vs `observed_applied` vs `rejected`/...) the
 * drawer surfaces distinctly rather than treating the POST response
 * as "done" (plan §7.3).
 */
export function useDoorCommand() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<DoorCommandResponse, Error, { name: string; mode: DoorRequest['mode'] }>({
    mutationFn: (vars) =>
      doorCommand(siteId as SiteId, vars.name, {
        action: vars.mode,
        idempotency_key: crypto.randomUUID(),
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: doorsQueryOptions(siteId ? (siteId as SiteId) : null).queryKey }),
  });
}

// ----------------------------------------------------------------------------
// Manual-open override (W8b)
//
// NEITHER of these invalidates the doors query, and that is not an
// oversight. There is no projection for an override: the record lives
// in the site's lifecycle broker, `rmf-door-state-mux` reads it there
// and synthesizes MODE_OPEN, and nothing republishes "this door is
// overridden" onto any key the worker projects. A refetch would return
// the same snapshot and invite the reader to believe it confirmed
// something. What the door reports afterwards -- MODE_OPEN, synthesized
// on the mux's 1 s timer -- arrives over the ordinary live overlay
// within a second or two, with no help from here.
// ----------------------------------------------------------------------------

export interface DeclareDoorOpenVariables {
  door: string;
  attestation: DoorOverrideAttestation;
  /** Seconds. Optional; the contract defaults it. The api-server CLAMPS
   *  rather than refusing, so read the granted value back off
   *  `response.command.target.ttl_s`. */
  ttlS?: number;
}

/**
 * `POST /api/v1/sites/{site}/doors/{door}/override` -- declare a door
 * manually open (`door.declare_open`).
 *
 * The idempotency key is minted PER PRESS (`crypto.randomUUID()`), not
 * derived. The lift verbs derive theirs so a double-tap replays one
 * record instead of publishing twice; here a second press is a
 * deliberate act with a fresh TTL -- an operator extending an override
 * that is about to lapse -- and replaying the first record would
 * answer that press with the old grant and let the door close under a
 * robot. The double-tap this leaves open costs an extra broker write
 * of the same record, which is atomic and idempotent by nature.
 */
export function useDeclareDoorOpen() {
  const siteId = useSafeSiteId();
  return useMutation<DoorOverrideResponse, Error, DeclareDoorOpenVariables>({
    mutationFn: (vars) =>
      declareDoorOpen(siteId as SiteId, vars.door, {
        attestation: vars.attestation,
        ...(vars.ttlS === undefined ? {} : { ttl_s: vars.ttlS }),
        idempotency_key: crypto.randomUUID(),
      }),
  });
}

/** `DELETE .../override?idempotency_key=...` -- end an override early
 *  (`door.revoke_override`). No body: see `revokeDoorOverride`. */
export function useRevokeDoorOverride() {
  const siteId = useSafeSiteId();
  return useMutation<DoorOverrideResponse, Error, { door: string }>({
    mutationFn: (vars) =>
      revokeDoorOverride(siteId as SiteId, vars.door, crypto.randomUUID()),
  });
}

// ----------------------------------------------------------------------------
// Write gate (tier 1 `inventory_ok` + tier 2 `armed`, per ADAPTER)
// ----------------------------------------------------------------------------

/**
 * Every door adapter's write gate at this site.
 *
 * ONE query for the screen. `DoorWriteGatePanel` renders above the
 * doors table AND inside `DoorDetailDrawer`, and both read this hook,
 * so the panel an operator sees while looking at one door cannot
 * disagree with the panel they saw a moment earlier over the table.
 *
 * `DoorWriteGate[] | null`, and callers must keep the three apart:
 * `null` is a settled 404 (this api-server publishes no door write
 * gate), `[]` is a readable answer meaning this site has no door
 * adapter, and `undefined` is the query still in flight or failed.
 * Only two of those are readings, and only one of them is a gate.
 */
export function useDoorWriteGates() {
  const siteId = useSafeSiteId();
  return useQuery<DoorWriteGate[] | null>(
    doorWriteGatesQueryOptions(siteId ? (siteId as SiteId) : null),
  );
}

/**
 * Per-door health for this site, keyed by door name.
 *
 * WHY A SEPARATE HOOK RATHER THAN A FIELD ON `useDoors`. The doors key
 * is not that hook's alone — `useLiveData` writes the SSE overlay
 * straight into it — so anything merged inside its queryFn is replaced
 * by the next live event. The same trap `mergeDoorsWithRegistry`
 * already works around. Reading health separately and joining at the
 * call site is the only shape that survives an event arriving.
 *
 * Returns a Map so the doors table can look a door up without scanning,
 * and `null` when the api-server has no health read path at all — which
 * the caller must keep apart from `[]` ("nothing publishes health
 * here") and `undefined` ("still loading, or failed").
 */
export function useDoorHealth() {
  const siteId = useSafeSiteId();
  const query = useQuery<DoorHealth[] | null>(
    doorHealthQueryOptions(siteId ? (siteId as SiteId) : null),
  );
  const byDoor = useMemo(() => {
    if (!query.data) return null;
    const out = new Map<string, DoorHealth>();
    // Last writer wins, and a site with two adapters claiming one door
    // name is a misconfiguration worth seeing rather than merging: the
    // table shows one of them and the drawer names the adapter, so the
    // disagreement is visible instead of averaged away.
    for (const h of query.data) out.set(h.door, h);
    return out;
  }, [query.data]);
  return { ...query, byDoor };
}

export interface ArmDoorWritesVariables {
  /** Which adapter to arm. Never defaulted to "the only one": a site
   *  that grows a second door adapter must not have this silently aim
   *  at the first (decision D1). */
  adapterId: string;
  body: DoorArmRequest;
}

/**
 * Arm or disarm one door adapter.
 *
 * Invalidates the doors query alongside the gate, and that is not
 * housekeeping. `translate.door_mode` returns MODE_OFFLINE for every
 * door the adapter owns while `armed` is false — the disarm is one of
 * the conditions in that check — so leaving the door rows on their
 * pre-arm snapshot would show an "Armed" gate above doors still
 * reading offline, or the reverse, which is the disagreement this
 * panel exists to prevent.
 *
 * Nothing here re-sends an arm, ever (invariant I1). A mutation the
 * user pressed is the only thing that puts a `door.arm_writes` intent
 * on the wire: no retry, no reconciler, no replay on mount. A machine
 * that can re-arm is a deployment that can ship pre-armed.
 */
export function useArmDoorWrites() {
  const siteId = useSafeSiteId();
  const qc = useQueryClient();
  return useMutation<DoorArmResponse, Error, ArmDoorWritesVariables>({
    mutationFn: (vars) => armDoorWrites(siteId as SiteId, vars.adapterId, vars.body),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: doorWriteGatesQueryOptions(siteId ? (siteId as SiteId) : null).queryKey,
      });
      void qc.invalidateQueries({
        queryKey: doorsQueryOptions(siteId ? (siteId as SiteId) : null).queryKey,
      });
    },
  });
}
